import { Flex, Grid, Stack, Switch, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { get } from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';

import { Declarant, Declaration, DeclarationHighValueGroupType, EnvironmentCountries, EORIGIN_DATE_FORMAT } from '@e-origin/shared';

import { DatePickerInput, EucdmCode, HideElementFor, MultiLangCodes, MultiSelect, Select, TextInput } from '@components';
import { ALLOWED_HV_REQUESTED_PROCEDURES } from '@constants';
import { retrieveCodes } from '@hooks/retrieveCodes';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { disableAmendmentField } from '@utils';
import { DeclarationGeneralFormValues } from './validateDeclarationGeneralForm';

const MESSAGE_INFO_DECLARATION_MAIN_TYPE = 'messageInfo.declarationMainType';
const MESSAGE_INFO_DECLARATION_TYPE = 'messageInfo.declarationType';
const MESSAGE_INFO_SECURITY = 'messageInfo.security';
const MESSAGE_INFO_ACCEPTANCE_DATE = 'messageInfo.acceptanceDate';
const CUSTOMS_PROCEDURE_CURRENT = 'customsProcedureCurrent';
const CUSTOMS_PROCEDURE_PREVIOUS = 'customsProcedurePrevious';
const GOOD_SHIPMENT_PREFERENCE = 'goodShipmentGlobal.preference';
const OTHER_DATA_NATURE_OF_TRANSACTION = 'otherDataElements.natureOfTransaction';
const DELIVERY_TERMS_INCOTERM_CODE = 'valuationInformationTaxes.deliveryTerms.incotermCode';
const DELIVERY_TERMS_COUNTRY = 'valuationInformationTaxes.deliveryTerms.country';

interface GlobalAndProcedureSectionProps {
  form: UseFormReturnType<DeclarationGeneralFormValues>;
  editDisabled?: boolean;
  declarationTypeDisabled?: boolean;
  isExport?: boolean;
  declarant?: Declarant;
  declaration?: Declaration;
  declarationGroup: string;
}

const ADDITIONAL_PROCEDURE_PATH = 'declarationGoods.additionalProcedure';

export const GlobalAndProcedureSection: React.FC<GlobalAndProcedureSectionProps> = ({
  form,
  editDisabled,
  declarationTypeDisabled,
  isExport,
  declarant,
  declaration,
  declarationGroup,
}) => {
  const { data: settings } = useSettings();
  const isNetherlands = settings?.country === EnvironmentCountries.NL;

  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport,
  });

  const Switcher = (props: { field: string; size?: string; disabled?: boolean; label?: string }) => (
    <Switch
      {...form.getInputProps(props.field)}
      checked={get(form.values, props.field)}
      disabled={editDisabled || props.disabled}
      label={props.label}
      size={props.size || 'xs'}
      mr="sm"
    />
  );

  const selectedMessageType = form.values.generalInfo.group;
  const allowedProcedures = ALLOWED_HV_REQUESTED_PROCEDURES[selectedMessageType] ?? [];

  useEffect(() => {
    if (form.values.generalInfo.group === DeclarationHighValueGroupType.H5 && !form.values.declarationGoods.additionalProcedure?.includes('F15')) {
      form.setFieldValue(ADDITIONAL_PROCEDURE_PATH, [...form.values.declarationGoods.additionalProcedure, 'F15']);
    }
  }, [form.values.generalInfo.group]);

  return (
    <Stack>
      <Stack mb="xl">
        <Title order={5} fw={600}>
          Global Parameter
        </Title>

        <Grid>
          <HideElementFor systems={['DECO', 'IDMS_LV']} declarationGroup={declarationGroup}>
            <Grid.Col span={6}>
              <EucdmCode path={MESSAGE_INFO_DECLARATION_MAIN_TYPE}>
                <MultiLangCodes
                  {...form.getInputProps(MESSAGE_INFO_DECLARATION_MAIN_TYPE)}
                  label="Declaration Main Type"
                  code="CL231"
                  system={codelistSystems}
                  onChange={(selectedOption) => form.setFieldValue(MESSAGE_INFO_DECLARATION_MAIN_TYPE, selectedOption)}
                  disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
                />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
          <Grid.Col span={6}>
            <EucdmCode path={MESSAGE_INFO_DECLARATION_TYPE}>
              <MultiLangCodes
                {...form.getInputProps(MESSAGE_INFO_DECLARATION_TYPE)}
                label="Declaration Type"
                code="CL042"
                system={codelistSystems}
                onChange={(selectedOption) => form.setFieldValue(MESSAGE_INFO_DECLARATION_TYPE, selectedOption)}
                disabled={
                  declarationTypeDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })
                }
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        <Grid>
          <HideElementFor systems={['IDMS_LV']} declarationGroup={declarationGroup}>
            <Grid.Col span={6}>
              <EucdmCode path={'consignment.referenceNumberUCR'}>
                <TextInput
                  {...form.getInputProps('consignment.referenceNumberUCR')}
                  label="Commercial reference"
                  disabled={editDisabled}
                  rightSection={<Switcher field={'consignment.referenceNumberUCRHeader'} />}
                />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
          <HideElementFor systems={['DMS', 'DECO', 'IDMS_LV']} declarationGroup={declarationGroup} condition={() => isNetherlands}>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('consignment.customsOfficePresentation.issuingPlaceCode')} label="Place of Issue" disabled={editDisabled} />
            </Grid.Col>
          </HideElementFor>
        </Grid>

        <HideElementFor systems={['DMS', 'DECO', 'IDMS_LV']} declarationGroup={declarationGroup}>
          <Grid>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('otherDataElements.registrationNumber')} label="Registration number" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={6}>
              <EucdmCode path={MESSAGE_INFO_ACCEPTANCE_DATE}>
                <DatePickerInput
                  {...form.getInputProps(MESSAGE_INFO_ACCEPTANCE_DATE)}
                  label="Acceptance Date"
                  value={form.values.messageInfo.acceptanceDate ? moment(form.values.messageInfo.acceptanceDate, EORIGIN_DATE_FORMAT).toDate() : null}
                  onChange={(value: Date) =>
                    form.setFieldValue(MESSAGE_INFO_ACCEPTANCE_DATE, value ? moment(value, EORIGIN_DATE_FORMAT).format(EORIGIN_DATE_FORMAT) : null)
                  }
                  disabled={editDisabled}
                  clearable
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        </HideElementFor>

        <Grid>
          {isExport && (
            <Grid.Col span={6}>
              <EucdmCode path={MESSAGE_INFO_SECURITY}>
                <MultiLangCodes
                  {...form.getInputProps(MESSAGE_INFO_SECURITY)}
                  label="Security"
                  code="CL217"
                  system={codelistSystems}
                  onChange={(selectedOption) => form.setFieldValue(MESSAGE_INFO_SECURITY, Number(selectedOption))}
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
          )}
          <HideElementFor systems={['DECO', 'IDMS_LV']} declarationGroup={declarationGroup}>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('customsState.bcpMrn')} label="BCP MRN" size="lg" disabled={editDisabled} />
            </Grid.Col>
          </HideElementFor>
        </Grid>
      </Stack>

      <HideElementFor systems={['IDMS_LV', 'DECO']} declarationGroup={declarationGroup}>
        <Stack mb="xl">
          <Flex justify="space-between" align="center">
            <Title order={5} fw={600}>
              Procedure
            </Title>
            <Switcher field={'goodShipmentGlobal.procedureHeader'} size="lg" label="Header" />
          </Flex>
          <Grid>
            <HideElementFor systems={['DMS']} declarationGroup={declarationGroup}>
              <Grid.Col span={4}>
                <Select
                  {...form.getInputProps('generalInfo.group')}
                  label="Message type"
                  placeholder="Select a message type"
                  data={Object.values(DeclarationHighValueGroupType).map((value) => ({ value, label: value }))}
                  disabled={editDisabled}
                  searchable
                />
              </Grid.Col>
            </HideElementFor>
            <Grid.Col span={4}>
              <EucdmCode path="goodShipmentGlobal.customsProcedure.current">
                <MultiLangCodes
                  {...form.getInputProps(CUSTOMS_PROCEDURE_CURRENT)}
                  label="Requested procedure"
                  code="CL092"
                  includeList={allowedProcedures.length > 0 ? allowedProcedures : undefined}
                  system={codelistSystems}
                  onChange={(selectedOption) => form.setFieldValue(CUSTOMS_PROCEDURE_CURRENT, selectedOption)}
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={4}>
              <EucdmCode path="goodShipmentGlobal.customsProcedure.previous">
                <MultiLangCodes
                  {...form.getInputProps(CUSTOMS_PROCEDURE_PREVIOUS)}
                  label="Previous procedure"
                  code="CL093"
                  system={codelistSystems}
                  onChange={(selectedOption) => form.setFieldValue(CUSTOMS_PROCEDURE_PREVIOUS, selectedOption)}
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={4}>
              <EucdmCode path={GOOD_SHIPMENT_PREFERENCE}>
                <MultiLangCodes
                  {...form.getInputProps(GOOD_SHIPMENT_PREFERENCE)}
                  label="Preference"
                  code="CL603"
                  system={codelistSystems}
                  onChange={(selectedOption) => form.setFieldValue(GOOD_SHIPMENT_PREFERENCE, selectedOption)}
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={4}>
              <EucdmCode path="goodsShipmentItems.additionalProcedure">
                <MultiSelect
                  {...form.getInputProps(ADDITIONAL_PROCEDURE_PATH)}
                  onChange={(value) => form.setFieldValue(ADDITIONAL_PROCEDURE_PATH, value)}
                  searchable
                  clearable
                  hidePickedOptions
                  label="Additional procedure"
                  data-pw="additional-procedure-input"
                  fetchOptions={async (search: string) => await retrieveCodes('AdditionalProcedure', search, declarant.language)}
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>

          <HideElementFor systems={['DMS', 'DECO']} declarationGroup={declarationGroup}>
            <Grid>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('goodShipmentGlobal.customsProcedureType')} label="Procedure Type" disabled={editDisabled} />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('goodShipmentGlobal.customsNationalProcedure')} label="National procedure" disabled={editDisabled} />
              </Grid.Col>
              <Grid.Col span={4}>
                <MultiLangCodes
                  {...form.getInputProps('goodShipmentGlobal.customsNationalProcedureCode')}
                  label="National procedure code"
                  code="CL457"
                  system={codelistSystems}
                  onChange={(selectedOption) => form.setFieldValue('goodShipmentGlobal.customsNationalProcedureCode', selectedOption)}
                  disabled={editDisabled}
                />
              </Grid.Col>
            </Grid>
          </HideElementFor>
        </Stack>
      </HideElementFor>

      <HideElementFor systems={['DECO', 'IDMS_LV']} declarationGroup={declarationGroup} condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
        <Stack mb="xl">
          <Title order={5} fw={600}>
            Transaction nature / delivery terms
          </Title>

          <Grid>
            <Grid.Col span={4}>
              <EucdmCode path={OTHER_DATA_NATURE_OF_TRANSACTION}>
                <MultiLangCodes
                  {...form.getInputProps(OTHER_DATA_NATURE_OF_TRANSACTION)}
                  label="Nature of transaction"
                  code="CL091"
                  system={codelistSystems}
                  onChange={(selectedOption) => form.setFieldValue(OTHER_DATA_NATURE_OF_TRANSACTION, selectedOption)}
                  disabled={editDisabled}
                  rightSection={<Switcher field="otherDataElements.natureOfTransactionHeader" />}
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <EucdmCode path={DELIVERY_TERMS_INCOTERM_CODE}>
                <MultiLangCodes
                  {...form.getInputProps(DELIVERY_TERMS_INCOTERM_CODE)}
                  label="Delivery terms"
                  code="CL090"
                  system={codelistSystems}
                  onChange={(selectedOption) => form.setFieldValue(DELIVERY_TERMS_INCOTERM_CODE, selectedOption)}
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={4}>
              <EucdmCode path={'valuationInformationTaxes.deliveryTerms.location'}>
                <TextInput {...form.getInputProps('valuationInformationTaxes.deliveryTerms.location')} label="Delivery Terms Place" disabled={editDisabled} />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={4}>
              <EucdmCode path={DELIVERY_TERMS_COUNTRY}>
                <MultiLangCodes
                  {...form.getInputProps(DELIVERY_TERMS_COUNTRY)}
                  label="Delivery terms country"
                  code="CL199"
                  system={codelistSystems}
                  onChange={(selectedOption) => form.setFieldValue(DELIVERY_TERMS_COUNTRY, selectedOption)}
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        </Stack>
      </HideElementFor>
    </Stack>
  );
};
